import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import meetIcon from "../../../assets/img/mobileLanding/meet.png";
import useDevice from 'hooks/useDevice';
import { Link } from 'react-router-dom';
import { paths } from 'routes/routes/paths';

interface CTAButtonsProps {
    /** Custom text for the schedule button */
    scheduleButtonText?: any;
    /** Custom text for the WhatsApp button */
    whatsAppButtonText?: any;
    /** Custom helper text for schedule button */
    scheduleHelperText?: any;
    /** Custom helper text for WhatsApp button */
    whatsAppHelperText?: any;
    /** WhatsApp link/phone number */
    whatsAppLink?: string;
    /** Calendly or scheduling link */
    scheduleLink?: string;
    /** Hide schedule button */
    hideScheduleButton?: boolean;
    /** Hide WhatsApp button */
    hideWhatsAppButton?: boolean;
    /** Container styling */
    containerSx?: object;

    textColor?: string;
    b2style?: string; // outline or contained
    direction?: 'row' | 'column';
    /** Text alignment for schedule helper text */
    scheduleHelperTextAlign?: 'left' | 'center' | 'right';
    /** Text alignment for WhatsApp helper text */
    whatsAppHelperTextAlign?: 'left' | 'center' | 'right';
}

const CTAButtons: React.FC<CTAButtonsProps> = ({
    scheduleButtonText = "Schedule a Project Discussion",
    whatsAppButtonText = "Chat On WhatsApp",
    scheduleHelperText = "Pick a time that suits you. We'll discuss your business, not push a sales pitch.",
    whatsAppHelperText = "Have a quick question? Our team will reply on WhatsApp.",
    whatsAppLink = "https://wa.me/+9109562110055",
    hideScheduleButton = false,
    hideWhatsAppButton = false,
    containerSx = {},
    textColor = '#666',
    b2style = "contained",
    direction = 'column',
    scheduleHelperTextAlign = 'center',
    whatsAppHelperTextAlign = 'center'
}) => {
    const { screenWidth } = useDevice();


    const handleWhatsAppClick = () => {
        if (whatsAppLink) {
            window.open(whatsAppLink, '_blank');
        }
    };

    const isRow = direction === 'row';

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: direction,
            gap: isRow ? 5 : 2,
            alignItems: isRow ? 'flex-start' : 'center',
            width: '100%',
            maxWidth: screenWidth < 770 ? "430px" : "auto",
            margin: "0 auto",
            position: 'relative',
            zIndex: 10,
            ...containerSx
        }}>
            {/* Schedule Button */}
            {!hideScheduleButton && (
                <Box sx={{
                    flex: isRow ? 1 : 'unset',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    width: isRow ? '100%' : 'auto',
                    alignItems: isRow ? 'flex-start' : 'stretch',
                    maxWidth: '430px',
                }}>
                    <Link to={paths.public.schedule} style={{ width: "100%" }} >
                        <Button
                            variant="contained"
                            startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: '20px' }} />}
                            className="font-satoshi-bold"
                            sx={{
                                backgroundColor: '#0044FF',
                                color: '#fff',
                                fontSize: isRow ? '16px' : (screenWidth < 330 ? '14px' : '16px'),
                                fontWeight: 700,
                                padding: isRow ? '14px 24px' : (screenWidth < 330 ? '12px 24px' : '16px 24px'),
                                borderRadius: '12px',
                                textTransform: 'none',
                                boxShadow: 'none',
                                width: '100%',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                    backgroundColor: '#0033CC',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {scheduleButtonText}
                        </Button>
                    </Link>

                    <Box sx={{ width: '100%' }}>
                        <Typography
                            className="font-satoshi-regular"
                            sx={{
                                color: textColor,
                                fontSize: isRow ? '13px' : '12px',
                                fontWeight: 400,
                                display: 'flex',
                                alignItems: 'start',
                                justifyContent:
                                    (scheduleHelperTextAlign === 'left' ? 'flex-start' :
                                        scheduleHelperTextAlign === 'right' ? 'flex-end' : 'center'),
                                gap: 0.5,
                                px: isRow ? 0 : 1,
                            }}
                        >
                            <img
                                src={meetIcon}
                                alt="meet-icon"
                                style={{ width: "14px", height: "14px", marginTop: '2px', flexShrink: 0 }}
                            />
                            {scheduleHelperText}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* WhatsApp Button */}
            {!hideWhatsAppButton && (
                <Box sx={{
                    flex: isRow ? 1 : 'unset',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    width: '100%',
                    alignItems: isRow ? 'flex-start' : 'stretch',
                    maxWidth: "460px"
                }}>
                    <Button
                        variant="outlined"
                        startIcon={<WhatsAppIcon sx={{ color: '#21970C', fontSize: '20px' }} />}
                        className="font-satoshi-bold"
                        onClick={handleWhatsAppClick}
                        sx={{
                            backgroundColor: b2style === 'contained' ? '#fff' : 'transparent',
                            color: '#21970C',
                            fontSize: isRow ? '16px' : (screenWidth < 330 ? '14px' : '16px'),
                            fontWeight: 700,
                            padding: isRow ? '14px 24px' : (screenWidth < 330 ? '12px 24px' : '16px 24px'),
                            borderRadius: '12px',
                            border: '1px solid #21970C',
                            textTransform: 'none',
                            width: '100%',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                                backgroundColor: '#E8F8ED',
                                border: '1px solid #21970C',
                            },
                        }}
                    >
                        {whatsAppButtonText}
                    </Button>

                    <Typography
                        className="font-satoshi-regular"
                        sx={{
                            color: textColor,
                            fontSize: isRow ? '13px' : '12px',
                            fontWeight: 400,
                            textAlign: whatsAppHelperTextAlign,
                            width: '100%',
                            px: isRow ? 0 : 1,
                        }}
                    >
                        {whatsAppHelperText}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default CTAButtons;
